// ============================================================
// CLUBHUB – Dokumente: Vorschauen
// ============================================================
// Was eine Datei an der Stelle ihrer Vorschau zeigt — klein in der
// Kachel und der Zeile, gross im Seitenblatt.
//
// MIT SITZUNG hat jede Datei eine Adresse (`adresse`,
// `/api/dokumente/<id>`), die Rechte prueft der Server: Bilder
// zeigen sich echt, PDF im eingebetteten Betrachter, Video im
// Spieler (Range-Anfragen). Fuer Word, Excel und Co. gibt es keine
// Vorschau — sie laden herunter. Eine verkleinerte Ableitung fuer die
// Kacheln gibt es noch nicht; Bilder laden in voller Groesse (lazy).
//
// MUSTERBESTAND: Es gibt keine echten Dateien. Bilder zeichnen sich
// deshalb aus einem `Motiv` (eine kleine, erfundene Szene in den
// Diagrammfarben der Foundation — Sommerfest, Spielfeld,
// Mannschaftsfoto, Weihnachtsfeier, Vereinslogo), Dokumente als
// Platzhalterseite mit Balken statt Text. Eben vom Geraet gewaehlte
// Bilder zeigen sich echt (`lokaleVorschau`, eine blob:-Adresse).
// ============================================================

"use client";

import { useState } from "react";
import {
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Folder,
  Play,
  Presentation,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { dateiArt, endungsLabel, grundname, type DateiArt } from "@/lib/dokumente/format";
import type { DocumentView, Motiv } from "@/lib/dokumente/views";

export const ART_ZEICHEN: Record<DateiArt, LucideIcon> = {
  pdf: FileText,
  text: FileText,
  tabelle: FileSpreadsheet,
  praesentation: Presentation,
  bild: FileImage,
  video: FileVideo,
  archiv: FileArchive,
  sonstige: File,
};

export function DateiZeichen({ datei, className }: { datei: Pick<DocumentView, "mimeType" | "name">; className?: string }) {
  const Zeichen = ART_ZEICHEN[dateiArt(datei.mimeType, datei.name)];
  return <Zeichen className={className} aria-hidden />;
}

// ── Zufall mit festem Startwert (wie lib/mock/core, ohne dessen Bestand) ──
function zufall(seed: number) {
  let a = (seed * 2654435761) >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    // Auf drei Stellen: was daraus ins SVG geht, schreibt jede Umgebung gleich.
    return Math.round((((t ^ (t >>> 14)) >>> 0) / 4294967296) * 1000) / 1000;
  };
}

const TRIKOT = ["fill-chart-2", "fill-chart-5", "fill-chart-4", "fill-chart-akzent", "fill-chart-1"];

/** Eine erfundene Szene im Format 4:3. */
export function Motivbild({ motiv, className }: { motiv: Motiv; className?: string }) {
  const r = zufall(motiv.seed + 11);
  const svg = (inhalt: React.ReactNode, grund?: string) => (
    <svg viewBox="0 0 160 120" preserveAspectRatio="xMidYMid slice" className={cn("h-full w-full", className)} aria-hidden>
      {grund && <rect width="160" height="120" className={grund} />}
      {inhalt}
    </svg>
  );

  switch (motiv.art) {
    case "spielfeld": {
      const spieler = Array.from({ length: 7 }, () => ({ x: 14 + r() * 132, y: 16 + r() * 88, team: r() < 0.5 }));
      return svg(
        <>
          {Array.from({ length: 8 }, (_, i) => (
            <rect key={i} x={i * 20} y="0" width="10" height="120" className="fill-chart-3/15" />
          ))}
          <g className="stroke-bg-primary/80" fill="none" strokeWidth="1.2">
            <rect x="8" y="8" width="144" height="104" />
            <line x1="80" y1="8" x2="80" y2="112" />
            <circle cx="80" cy="60" r="15" />
            <rect x="8" y="36" width="20" height="48" />
            <rect x="132" y="36" width="20" height="48" />
          </g>
          {spieler.map((s, i) => (
            <g key={i}>
              <circle cx={s.x} cy={s.y - 5} r="2.6" className="fill-chart-6" />
              <rect x={s.x - 3} y={s.y - 2.5} width="6" height="8" rx="2" className={s.team ? "fill-chart-akzent/90" : "fill-chart-2"} />
            </g>
          ))}
          <circle cx={60 + r() * 40} cy={40 + r() * 40} r="2" className="fill-bg-primary" />
        </>,
        "fill-chart-3/70",
      );
    }
    case "mannschaft": {
      const farbe = TRIKOT[Math.floor(r() * 3)]!;
      return svg(
        <>
          <rect y="74" width="160" height="46" className="fill-chart-3/55" />
          <rect x="10" y="20" width="140" height="6" rx="1" className="fill-chart-1/15" />
          {Array.from({ length: 7 }, (_, i) => {
            const x = 20 + i * 20;
            return (
              <g key={`h${i}`}>
                <circle cx={x} cy="40" r="6" className="fill-chart-6" />
                <rect x={x - 8} y="47" width="16" height="26" rx="5" className={farbe} />
              </g>
            );
          })}
          {Array.from({ length: 6 }, (_, i) => {
            const x = 30 + i * 20;
            return (
              <g key={`v${i}`}>
                <circle cx={x} cy="66" r="6" className="fill-chart-6" />
                <rect x={x - 8} y="73" width="16" height="18" rx="5" className={farbe} />
                <rect x={x - 7} y="90" width="14" height="6" rx="2" className="fill-bg-primary/80" />
              </g>
            );
          })}
          <circle cx="80" cy="104" r="6" className="fill-bg-primary stroke-chart-1/40" strokeWidth="1" />
        </>,
        "fill-chart-2/15",
      );
    }
    case "abend": {
      // Gerundet: Server und Browser sollen dieselben Zahlen ins SVG schreiben.
      const lichter = Array.from({ length: 12 }, (_, i) => ({ x: 8 + i * 13, y: Math.round((16 + Math.sin(i / 1.8) * 6) * 100) / 100 }));
      const leute = Array.from({ length: 6 }, () => ({ x: 18 + r() * 124, s: 0.8 + r() * 0.4 }));
      return svg(
        <>
          <path d={`M0 14 ${lichter.map((l) => `L${l.x} ${l.y}`).join(" ")} L160 14`} className="stroke-neutral-500" fill="none" strokeWidth="0.6" />
          {lichter.map((l, i) => (
            <circle key={i} cx={l.x} cy={l.y + 2} r="2" className="fill-chart-4" />
          ))}
          <polygon points="128,92 142,52 156,92" className="fill-chart-3/80" />
          {[0, 1, 2, 3].map((i) => (
            <circle key={i} cx={134 + i * 4} cy={66 + (i % 2) * 12} r="1.6" className="fill-chart-4" />
          ))}
          <rect y="92" width="160" height="28" className="fill-neutral-700" />
          <rect x="14" y="84" width="96" height="5" rx="1" className="fill-neutral-600" />
          {leute.map((p, i) => (
            <g key={i} transform={`translate(${p.x} 80) scale(${p.s})`}>
              <circle cx="0" cy="-16" r="5" className="fill-neutral-500" />
              <rect x="-7" y="-10" width="14" height="18" rx="5" className="fill-neutral-600" />
            </g>
          ))}
        </>,
        "fill-neutral-800",
      );
    }
    case "logo": {
      const v = motiv.variante ?? "farbig";
      const schild = "M80 14 L120 25 L120 60 C120 84 102 99 80 107 C58 99 40 84 40 60 L40 25 Z";
      return svg(
        <>
          {v === "negativ" ? (
            <path d={schild} className="fill-none stroke-neutral-50" strokeWidth="3" />
          ) : (
            <path d={schild} className={v === "farbig" ? "fill-chart-3" : "fill-text-primary"} />
          )}
          {v === "farbig" && <path d="M40 62 L120 62 L120 70 L40 70 Z" className="fill-bg-primary/25" />}
          <text
            x="80"
            y="60"
            textAnchor="middle"
            fontSize="26"
            fontWeight="600"
            className={v === "negativ" ? "fill-neutral-50" : "fill-bg-primary"}
          >
            GA
          </text>
          <text x="80" y="86" textAnchor="middle" fontSize="10" letterSpacing="2" className={v === "negativ" ? "fill-neutral-50" : "fill-bg-primary/85"}>
            1860
          </text>
        </>,
        v === "negativ" ? "fill-neutral-900" : "fill-bg-primary",
      );
    }
    case "fest":
    default: {
      const wimpel = ["fill-chart-akzent/85", "fill-chart-4/90", "fill-chart-2/85", "fill-chart-3/85"];
      const leute = Array.from({ length: 9 }, () => ({ x: 6 + r() * 148, y: 88 + r() * 18, f: TRIKOT[Math.floor(r() * TRIKOT.length)]! }));
      const zelt = 18 + r() * 40;
      return svg(
        <>
          <circle cx={118 + r() * 24} cy="24" r="11" className="fill-chart-4/70" />
          <path d="M0 16 Q80 30 160 14" className="stroke-chart-1/40" fill="none" strokeWidth="0.7" />
          {Array.from({ length: 11 }, (_, i) => {
            const x = 4 + i * 15;
            const y = Math.round((16 + Math.sin((i / 10) * Math.PI) * 7) * 100) / 100;
            return <polygon key={i} points={`${x},${y} ${x + 9},${y} ${x + 4.5},${y + 9}`} className={wimpel[i % wimpel.length]} />;
          })}
          <rect y="80" width="160" height="40" className="fill-chart-3/45" />
          <rect x={zelt} y="56" width="58" height="26" className="fill-bg-primary" />
          <polygon points={`${zelt - 6},58 ${zelt + 29},36 ${zelt + 64},58`} className="fill-chart-akzent/75" />
          <rect x={zelt + 20} y="64" width="18" height="18" className="fill-chart-1/15" />
          <rect x={zelt + 70} y="74" width="40" height="4" rx="1" className="fill-chart-1/35" />
          {leute.map((p, i) => (
            <g key={i}>
              <circle cx={p.x} cy={p.y - 9} r="3.4" className="fill-chart-6" />
              <rect x={p.x - 4.5} y={p.y - 5.5} width="9" height="12" rx="3" className={p.f} />
            </g>
          ))}
        </>,
        "fill-chart-2/20",
      );
    }
  }
}

/** Ein Bild, das vom Geraet kam — faellt es aus (HEIC), steht das Zeichen da. */
function LokalesBild({ src, alt, className, ersatz }: { src: string; alt: string; className?: string; ersatz: React.ReactNode }) {
  const [kaputt, setKaputt] = useState(false);
  if (kaputt) return <>{ersatz}</>;
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt={alt} className={className} loading="lazy" decoding="async" onError={() => setKaputt(true)} />;
}

/** Die Adresse zum Ansehen im Browser (der Server entscheidet, ob die Art das darf). */
export function ansehAdresse(adresse: string): string {
  return `${adresse}${adresse.includes("?") ? "&" : "?"}ansehen=1`;
}

/** Ein echtes Bild aus dem Speicher (nur mit Sitzung). */
function echtesBild(d: DocumentView): string | null {
  return d.adresse && dateiArt(d.mimeType, d.name) === "bild" ? ansehAdresse(d.adresse) : null;
}

/** Hat die Datei ein Bild fuer die Kachel? */
export function hatBildvorschau(d: DocumentView): boolean {
  return Boolean(d.motiv || d.lokaleVorschau || echtesBild(d));
}

/** Der Inhalt der 4:3-Flaeche einer Kachel. */
export function Kachelvorschau({ datei }: { datei: DocumentView }) {
  const zeichen = (
    <span className="absolute inset-0 grid place-items-center">
      <span className="flex flex-col items-center gap-2">
        <DateiZeichen datei={datei} className="text-text-tertiary h-8 w-8" />
        <span className="text-caption text-text-tertiary font-medium tracking-wide">{endungsLabel(datei.name)}</span>
      </span>
    </span>
  );
  if (datei.lokaleVorschau) {
    return <LokalesBild key={datei.lokaleVorschau} src={datei.lokaleVorschau} alt="" className="h-full w-full object-cover" ersatz={zeichen} />;
  }
  const bild = echtesBild(datei);
  if (bild) return <LokalesBild key={bild} src={bild} alt="" className="h-full w-full object-cover" ersatz={zeichen} />;
  if (datei.motiv) return <Motivbild motiv={datei.motiv} />;
  return zeichen;
}

/** Klein vor dem Namen in der Liste: 36 Pixel, Bild oder Zeichen. */
export function Daumen({ datei, ordner }: { datei?: DocumentView; ordner?: boolean }) {
  return (
    <span className="bg-bg-secondary ring-border-subtle relative grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-lg ring-1 ring-inset">
      {ordner ? (
        <Folder className="text-text-tertiary h-4 w-4" aria-hidden />
      ) : datei && hatBildvorschau(datei) ? (
        <span className="absolute inset-0">
          <Kachelvorschau datei={datei} />
        </span>
      ) : datei ? (
        <DateiZeichen datei={datei} className="text-text-tertiary h-4 w-4" />
      ) : null}
    </span>
  );
}

// ── Grosse Vorschau im Seitenblatt ───────────────────────────
function Balken({ seed, zeilen, absaetze = 3 }: { seed: number; zeilen: number; absaetze?: number }) {
  const r = zufall(seed);
  const proAbsatz = Math.max(2, Math.round(zeilen / absaetze));
  return (
    <div className="space-y-[6%]">
      {Array.from({ length: absaetze }, (_, a) => (
        <div key={a} className="space-y-[2.2%]">
          {a > 0 && <div className="bg-text-tertiary/25 h-[5px] w-[34%] rounded-full" />}
          {Array.from({ length: proAbsatz }, (_, z) => (
            <div
              key={z}
              className="bg-bg-tertiary h-[4px] rounded-full"
              style={{ width: `${z === proAbsatz - 1 ? 40 + r() * 30 : 86 + r() * 14}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Eine Seite DIN A4 mit Kopf, Titel und Balken statt Text. */
export function Blattvorschau({ datei, clubName, seite = 1 }: { datei: DocumentView; clubName: string; seite?: number }) {
  const seed = datei.id.split("").reduce((s, c) => s + c.charCodeAt(0), 0) + seite * 31 + datei.version * 7;
  return (
    <div className="bg-bg-primary shadow-e2 mx-auto aspect-[210/297] w-full max-w-[280px] rounded-[3px] p-[9%] ring-1 ring-black/[0.06] dark:ring-white/[0.08]">
      <div className="flex items-center justify-between gap-2">
        <span className="text-text-tertiary truncate text-[8px] leading-none">{clubName}</span>
        <svg viewBox="0 0 160 120" className="h-4 w-5 shrink-0" aria-hidden>
          <path d="M80 14 L120 25 L120 60 C120 84 102 99 80 107 C58 99 40 84 40 60 L40 25 Z" className="fill-chart-3" />
        </svg>
      </div>
      <p className="text-text-primary mt-[12%] line-clamp-3 text-[11px] leading-snug font-medium">{grundname(datei.name)}</p>
      <div className="mt-[8%]">
        <Balken seed={seed} zeilen={14} absaetze={4} />
      </div>
    </div>
  );
}

function Tabellenvorschau({ datei }: { datei: DocumentView }) {
  const r = zufall(datei.size);
  return (
    <div className="bg-bg-primary shadow-e2 mx-auto w-full max-w-[420px] overflow-hidden rounded-md ring-1 ring-black/[0.06] dark:ring-white/[0.08]">
      <div className="text-caption text-text-tertiary border-border-subtle border-b px-3 py-1.5">{grundname(datei.name)}</div>
      <div className="grid grid-cols-[1.6fr_repeat(4,1fr)]">
        {Array.from({ length: 11 * 5 }, (_, i) => {
          const kopf = i < 5;
          return (
            <div key={i} className={cn("border-border-subtle h-6 border-r border-b px-1.5 py-2", kopf && "bg-bg-secondary")}>
              <div
                className={cn("h-[4px] rounded-full", kopf ? "bg-text-tertiary/30" : i % 5 === 0 ? "bg-bg-tertiary" : "bg-chart-2/20")}
                style={{ width: `${i % 5 === 0 ? 60 + r() * 35 : 30 + r() * 50}%`, marginLeft: i % 5 === 0 ? 0 : "auto" }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Folienvorschau({ datei }: { datei: DocumentView }) {
  return (
    <div className="bg-bg-primary shadow-e2 mx-auto flex aspect-video w-full max-w-[420px] flex-col rounded-md p-[6%] ring-1 ring-black/[0.06] dark:ring-white/[0.08]">
      <div className="bg-chart-3 h-1.5 w-10 rounded-full" />
      <p className="text-body-sm text-text-primary mt-3 font-medium">{grundname(datei.name)}</p>
      <div className="mt-auto">
        <Balken seed={datei.size} zeilen={4} absaetze={1} />
      </div>
    </div>
  );
}

function dauer(bytes: number): string {
  // Grob: 1,4 MB je Sekunde in Full HD.
  const s = Math.max(5, Math.round(bytes / 1_400_000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** Die grosse Vorschau oben im Seitenblatt. */
export function Grossvorschau({ datei, clubName }: { datei: DocumentView; clubName: string }) {
  const art = dateiArt(datei.mimeType, datei.name);
  const leer = (text: string) => (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <DateiZeichen datei={datei} className="text-text-tertiary h-10 w-10" />
      <p className="text-body-sm text-text-tertiary">{text}</p>
    </div>
  );

  // Mit Sitzung: die Datei selbst.
  if (datei.adresse) {
    const quelle = ansehAdresse(datei.adresse);
    if (art === "bild") {
      return (
        <LokalesBild
          key={quelle}
          src={quelle}
          alt={datei.name}
          className="max-h-[420px] w-full object-contain"
          ersatz={leer("Dieses Bildformat zeigt der Browser nicht an — herunterladen öffnet es.")}
        />
      );
    }
    if (art === "pdf") {
      return <iframe key={quelle} src={quelle} title={datei.name} className="h-[min(70vh,560px)] w-full border-0 bg-neutral-50" />;
    }
    if (art === "video") {
      return <video key={quelle} src={quelle} controls preload="metadata" playsInline className="aspect-video w-full bg-neutral-900" />;
    }
    if (art === "archiv") return leer("Ein Paket mit mehreren Dateien. Herunterladen und auf dem Gerät entpacken.");
    return leer("Für diesen Dateityp gibt es keine Vorschau im Browser — herunterladen öffnet ihn.");
  }

  if (art === "bild") {
    if (datei.lokaleVorschau) {
      return (
        <LokalesBild
          key={datei.lokaleVorschau}
          src={datei.lokaleVorschau}
          alt={datei.name}
          className="max-h-[360px] w-full object-contain"
          ersatz={leer("Dieses Bildformat zeigt der Browser nicht an — herunterladen öffnet es.")}
        />
      );
    }
    if (datei.motiv) {
      return (
        <div className="aspect-[4/3] w-full">
          <Motivbild motiv={datei.motiv} />
        </div>
      );
    }
    return leer("Noch keine Vorschau.");
  }
  if (art === "pdf" || art === "text") {
    return (
      <div className="w-full px-6 py-6">
        <Blattvorschau datei={datei} clubName={clubName} />
        {datei.pages !== null && (
          <p className="text-caption text-text-tertiary mt-3 text-center tabular-nums">
            Seite 1 von {datei.pages}
          </p>
        )}
      </div>
    );
  }
  if (art === "tabelle") {
    return (
      <div className="w-full px-4 py-6">
        <Tabellenvorschau datei={datei} />
      </div>
    );
  }
  if (art === "praesentation") {
    return (
      <div className="w-full px-4 py-6">
        <Folienvorschau datei={datei} />
      </div>
    );
  }
  if (art === "video") {
    return (
      <div className="relative grid aspect-video w-full place-items-center bg-neutral-900">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-neutral-50/15">
          <Play className="h-6 w-6 translate-x-0.5 text-neutral-50" aria-hidden />
        </span>
        <span className="text-caption absolute right-3 bottom-3 rounded bg-neutral-900/70 px-1.5 py-0.5 text-neutral-50 tabular-nums">
          {dauer(datei.size)}
        </span>
      </div>
    );
  }
  if (art === "archiv") return leer("Ein Paket mit mehreren Dateien. Herunterladen und auf dem Gerät entpacken.");
  return leer("Für diesen Dateityp gibt es keine Vorschau.");
}
